import { useState } from 'react';
import {
  Alert,
  App,
  Button,
  Descriptions,
  Empty,
  Popconfirm,
  Skeleton,
  Space,
  Table,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, SwapOutlined } from '@ant-design/icons';
import type { MembershipAssignment } from '@goelectric/shared-types';
import { BillingCycleBadge } from '@/features/membresias/components/MembershipBadges';
import { asignacionesApi } from '../api/asignacionesApi';
import { useMembershipAssignment } from '../hooks/useMembershipAssignment';
import { describeAssignmentError } from '../utils/errors';
import {
  formatCop,
  formatDateTime,
  formatDiscountPct,
  formatVigencia,
} from '../utils/format';
import { MembershipAssignmentStatusBadge } from './AssignmentStatusBadge';
import { AssignMembershipModal } from './AssignMembershipModal';
import { ReceiptsModal, type ReceiptsTarget } from './ReceiptsModal';

export interface MembershipAssignmentPanelProps {
  customerId: string;
  customerName?: string;
  canWrite: boolean;
  onChanged?: () => void;
}

/**
 * Sección de **Membresía** de la ficha del cliente (US-2.4, TG-53).
 *
 * El cliente tiene como máximo una membresía activa. `SUPER_ADMIN` puede asignar una
 * nueva (reemplaza la anterior, que queda cancelada en el historial) o cancelar la
 * activa; el resto de roles consulta. Cada asignación permite gestionar comprobantes.
 */
export function MembershipAssignmentPanel({
  customerId,
  customerName,
  canWrite,
  onChanged,
}: MembershipAssignmentPanelProps) {
  const { message } = App.useApp();
  const membership = useMembershipAssignment(customerId);
  const [assignOpen, setAssignOpen] = useState(false);
  const [receiptsTarget, setReceiptsTarget] = useState<ReceiptsTarget | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const cancel = async () => {
    setCancelling(true);
    try {
      await asignacionesApi.cancelMembership(customerId);
      message.success('Membresía cancelada. La asignación quedó en el historial.');
      membership.refresh();
      onChanged?.();
    } catch (caught) {
      message.error(describeAssignmentError(caught, 'No se pudo cancelar la membresía.'));
    } finally {
      setCancelling(false);
    }
  };

  const openReceipts = (assignment: MembershipAssignment) => {
    setReceiptsTarget({
      type: 'membership',
      assignmentId: assignment.id,
      label: `${assignment.membershipName} (${assignment.id})`,
    });
  };

  const historyColumns: ColumnsType<MembershipAssignment> = [
    {
      title: 'Membresía',
      key: 'membership',
      render: (_, assignment) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{assignment.membershipName}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontFamily: 'var(--ge-font-mono)', fontSize: 12 }}>
            {assignment.id}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Ciclo',
      key: 'cycle',
      width: 130,
      render: (_, assignment) => <BillingCycleBadge cycle={assignment.billingCycle} />,
    },
    {
      title: 'Descuento',
      key: 'discount',
      width: 120,
      align: 'right',
      render: (_, assignment) => formatDiscountPct(assignment.tariffDiscountPct),
    },
    {
      title: 'Vigencia',
      key: 'validity',
      width: 250,
      render: (_, assignment) => formatVigencia(assignment.validFrom, assignment.validTo),
    },
    {
      title: 'Estado',
      key: 'status',
      width: 130,
      render: (_, assignment) => (
        <MembershipAssignmentStatusBadge status={assignment.status} />
      ),
    },
    {
      title: 'Comprobantes',
      key: 'receipts',
      width: 150,
      align: 'right',
      render: (_, assignment) => (
        <Button
          size="small"
          aria-label={`Comprobantes de ${assignment.membershipName}`}
          onClick={() => openReceipts(assignment)}
        >
          Ver
        </Button>
      ),
    },
  ];

  return (
    <section aria-labelledby="membresia-asignada-title">
      <div className="clientes-page__header">
        <Typography.Title level={4} id="membresia-asignada-title" style={{ margin: 0 }}>
          Membresía
        </Typography.Title>
        {canWrite && (
          <Button
            type={membership.active ? 'default' : 'primary'}
            icon={membership.active ? <SwapOutlined /> : <PlusOutlined />}
            onClick={() => setAssignOpen(true)}
          >
            {membership.active ? 'Reemplazar membresía' : 'Asignar membresía'}
          </Button>
        )}
      </div>

      {membership.error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar la membresía del cliente"
          description={membership.error}
          action={
            <Button size="small" onClick={membership.refresh}>
              Reintentar
            </Button>
          }
        />
      ) : membership.loading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : membership.active ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Descriptions
            column={{ xs: 1, sm: 2 }}
            bordered
            size="small"
            title={
              <Space wrap>
                <Typography.Text strong>{membership.active.membershipName}</Typography.Text>
                <MembershipAssignmentStatusBadge status={membership.active.status} />
              </Space>
            }
          >
            <Descriptions.Item label="Identificador">
              <span style={{ fontFamily: 'var(--ge-font-mono)' }}>{membership.active.id}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Ciclo">
              <BillingCycleBadge cycle={membership.active.billingCycle} />
            </Descriptions.Item>
            <Descriptions.Item label="Descuento de tarifa">
              {formatDiscountPct(membership.active.tariffDiscountPct)}
            </Descriptions.Item>
            <Descriptions.Item label="Precio">
              {formatCop(membership.active.priceCop, membership.active.currency || 'COP')}
            </Descriptions.Item>
            <Descriptions.Item label="Vigencia" span={2}>
              {formatVigencia(membership.active.validFrom, membership.active.validTo)}
            </Descriptions.Item>
            <Descriptions.Item label="Asignada">
              {formatDateTime(membership.active.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Nota">
              {membership.active.note?.trim() || '—'}
            </Descriptions.Item>
          </Descriptions>

          <Space wrap>
            <Button onClick={() => openReceipts(membership.active as MembershipAssignment)}>
              Comprobantes
            </Button>
            {canWrite && (
              <Popconfirm
                title="Cancelar membresía"
                description="La membresía activa quedará cancelada y pasará al historial. Esta acción no se puede deshacer."
                okText="Cancelar membresía"
                okButtonProps={{ danger: true, loading: cancelling }}
                cancelText="Volver"
                onConfirm={() => void cancel()}
              >
                <Button danger loading={cancelling}>
                  Cancelar membresía
                </Button>
              </Popconfirm>
            )}
          </Space>
        </Space>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="El cliente no tiene una membresía activa."
        >
          {canWrite && (
            <Button type="primary" onClick={() => setAssignOpen(true)}>
              Asignar membresía
            </Button>
          )}
        </Empty>
      )}

      {membership.history.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Typography.Title level={5}>Histórico de membresías</Typography.Title>
          <Table<MembershipAssignment>
            rowKey="id"
            size="small"
            columns={historyColumns}
            dataSource={membership.history}
            pagination={false}
            scroll={{ x: 980 }}
            aria-label="Histórico de membresías del cliente"
          />
        </div>
      )}

      {canWrite && (
        <AssignMembershipModal
          open={assignOpen}
          customerId={customerId}
          customerName={customerName}
          activeName={membership.active?.membershipName}
          onClose={() => setAssignOpen(false)}
          onAssigned={() => {
            membership.refresh();
            onChanged?.();
          }}
        />
      )}

      <ReceiptsModal
        open={receiptsTarget !== null}
        customerId={customerId}
        target={receiptsTarget}
        canWrite={canWrite}
        onClose={() => setReceiptsTarget(null)}
      />
    </section>
  );
}
